import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import { errors, Log, RecaptchaEnterpriseActions } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createActiveStepError,
  createMessage,
  INvidiaShuttleLoginLandingModel,
  landingMessagesAtom,
  LandingStepEnum,
  LandingStepErrorsEnum,
  NvidiaShuttleLoginStepFormFieldsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
} from "@ryde/utils-landings";

import { useYupLoginLandingNvidiaShuttleValidationSchema } from "./parts";

export const useStepLoginNvidiaShuttle = () => {
  const { goToStep, setActiveStepErrors } = useLandingActions();
  const { requestPublicOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { yupLoginLandingNvidiaShuttleValidationSchema } =
    useYupLoginLandingNvidiaShuttleValidationSchema();

  const resolver = useYupValidationResolver(
    yupLoginLandingNvidiaShuttleValidationSchema,
    "landing",
  );

  const methods = useForm<INvidiaShuttleLoginLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      email: "",
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const emailValue = watch(NvidiaShuttleLoginStepFormFieldsEnum.Email);

  const isContinueCanBeApply = isValid && !!emailValue;

  const handleLogin = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    setApiLoading(true);
    setServerErrors(createMessage([]));
    const email = getValues(NvidiaShuttleLoginStepFormFieldsEnum.Email);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    try {
      const requestOtpResponse = await requestPublicOtp({
        allowExistingPassenger: true,
        recaptchaToken,
        email,
      });

      const { success, error } = requestOtpResponse ?? {};

      if (success) {
        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.VerificationStep, {
          email,
        });
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while requesting OTP", error);
      setActiveStepErrors(
        createActiveStepError(LandingStepErrorsEnum.GeneralError),
      );
      goToStep(LandingStepEnum.ErrorStep);
    } finally {
      setApiLoading(false);
    }
  });

  return { methods, handleLogin, isContinueCanBeApply };
};
